import { Text, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { saveForm, deleteForm, publishForm, closeForm, reopenForm, archiveForm, unarchiveForm, submitForm } from '@/data/forms';
import { supabase } from '@/data/client';
import { createAutomation } from '@/data/automations';
import { useLocalSearchParams, useRouter, useNavigation, useFocusEffect } from 'expo-router';
import { usePreventRemove } from 'expo-router/react-navigation';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import Toast from 'react-native-toast-message';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SolidButton from '@/components/General/SolidButton';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { createEmptySchema, schemaHasFieldType } from '@/utils/formsSchema';
import FormBuildTab from '@/components/Forms/builder/FormBuildTab';
import FormSettingsTab from '@/components/Forms/builder/FormSettingsTab';
import FormShareTab from '@/components/Forms/builder/FormShareTab';
import FormEntriesTab from '@/components/Forms/builder/FormEntriesTab';
import FormView from '@/components/Forms/FormView';
import SheetModal from '@/components/General/SheetModal';
import { saveToRecents } from '@/utils/recentlyVisited';
import { canDeleteForm, canEditForm, canFillForm, canManageFormEntries } from '@/utils/formPermissions';

const STATUS_CONFIG = {
    draft: { label: 'Draft', colorVariant: 'textSecondary' },
    published: { label: 'Published', colorVariant: 'success' },
    closed: { label: 'Closed', colorVariant: 'warning' },
    archived: { label: 'Archived', colorVariant: 'textSecondary' },
};

const TAB_ROUTES = [
    { key: 'build', title: 'Build' },
    { key: 'settings', title: 'Settings' },
    { key: 'share', title: 'Share' },
    { key: 'entries', title: 'Entries' },
];

const rolesFromForm = (form) => (form?.FormRoles || []).map((fr) => ({
    id: fr.roleID,
    name: fr.Role?.name,
    canView: fr.canView ?? true,
    canEdit: fr.canEdit ?? false,
    canManageEntries: fr.canManageEntries ?? false,
}));

// The `originalValues` snapshot must have the same normalized shape as the
// working draft (`details` minus `originalValues`) — comparing a draft that
// carries `Roles`/normalized `schema` against the raw row reports dirty when
// nothing changed. Form rows from mutations don't join FormRoles, so callers
// pass the current Roles/schema as fallbacks.
const formStoreSnapshot = (form, fallbackRoles = [], fallbackSchema = null) => ({
    ...form,
    Roles: form?.FormRoles ? rolesFromForm(form) : fallbackRoles,
    schema: form?.schema || fallbackSchema || createEmptySchema(),
});

export default function FormDetailScreen() {
    const { formID, mode, tab } = useLocalSearchParams();
    const [tabIndex, setTabIndex] = useState(() => Math.max(0, TAB_ROUTES.findIndex((route) => route.key === tab)));
    const [previewOpen, setPreviewOpen] = useState(false);

    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { isNative, isLandscape, isWeb } = usePlatform();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: form, isFetching } = Query.forms.byId({ id: formID });

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData);
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData);
    const details = useEditScreenStore(s => s.CreateFormModal?.[formID]) || null;

    // Initialize store from the fetched form
    useEffect(() => {
        if (form && !details) {
            const snapshot = formStoreSnapshot(form);
            setEditScreenData('CreateFormModal', formID, {
                ...snapshot,
                originalValues: snapshot,
            });
        }
    }, [form, formID]);

    // Refresh entries when returning from enrollment screen
    useFocusEffect(useCallback(() => {
        if (formID) {
            queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byFormId(formID) });
        }
    }, [formID, queryClient]));

    // Track recently visited
    useEffect(() => {
        if (form?.id) {
            saveToRecents({
                id: form.id,
                url: `/forms/${form.id}`,
                title: form.title,
                params: { formID: form.id },
            });
        }
    }, [form?.id]);

    const canEdit = useMemo(() => canEditForm(form, loggedInUser), [loggedInUser, form]);

    const canDelete = useMemo(() => canDeleteForm(form, loggedInUser), [loggedInUser, form]);

    const canManageEntries = useMemo(() => canManageFormEntries(form, loggedInUser), [loggedInUser, form]);

    const canSubmit = useMemo(() => canFillForm(form, loggedInUser), [loggedInUser, form]);

    // Entries tab only exists for members who can manage entries.
    const tabRoutes = useMemo(
        () => TAB_ROUTES.filter((route) => route.key !== 'entries' || canManageEntries),
        [canManageEntries],
    );

    const saveFormMutation = useMutation({
        mutationFn: saveForm,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.forms.byId(formID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.forms.all() });
            // Refresh store with the saved result. The mutation response is the
            // bare Forms row (no FormRoles join), so fall back to the current
            // Roles/schema — which were just saved — to keep them populated.
            if (data) {
                const snapshot = formStoreSnapshot(data, details?.Roles || [], details?.schema);
                setEditScreenData('CreateFormModal', formID, {
                    ...snapshot,
                    originalValues: snapshot,
                });
            }
        },
    });

    const deleteFormMutation = useMutation({
        mutationFn: () => deleteForm({ formID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.forms.all() });
            removeEditScreenData('CreateFormModal', formID);
            router.back();
        },
    });

    const lifecycleMutation = useMutation({
        mutationFn: async ({ action }) => {
            if (action === 'publish') return publishForm(formID);
            if (action === 'close') return closeForm(formID);
            if (action === 'archive') return archiveForm(formID);
            if (action === 'unarchive') return unarchiveForm(formID);
            if (action === 'reopen') return reopenForm(formID);
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.forms.byId(formID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.forms.all() });
            // Sync the edit-screen store so tabs (Share, Settings) reflect the
            // new status immediately — otherwise `details` stays stale and the
            // Share tab still shows the old lifecycle button.
            if (data) {
                const snapshot = formStoreSnapshot(data, details?.Roles || [], details?.schema);
                setEditScreenData('CreateFormModal', formID, {
                    ...snapshot,
                    originalValues: snapshot,
                });
            }
        },
    });

    // Header actions
    useEffect(() => {
        const options = [];
        if (form?.id) {
            options.push({
                name: 'Save to Shortcuts',
                icon: dropdownIcons.favorite,
                onSelect: () => {},
            });
        }
        if (canEdit) {
            options.push(form.status === 'archived'
                ? {
                    name: 'Unarchive Form',
                    icon: dropdownIcons.unarchive,
                    onSelect: () => lifecycleMutation.mutate({ action: 'unarchive' }),
                }
                : {
                    name: 'Archive Form',
                    icon: dropdownIcons.archive,
                    onSelect: () => {
                        showAlert({
                            title: 'Archive Form',
                            subtitle: `Archive "${form?.title}"? It will be hidden from members and stop accepting responses. You can unarchive it later — it will return as a draft.`,
                            isDestructive: true,
                            confirmText: 'Archive',
                            onConfirm: () => lifecycleMutation.mutate({ action: 'archive' }),
                        });
                    },
                });
        }
        if (canDelete) {
            options.push({
                name: 'Delete Form',
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => {
                    showAlert({
                        title: 'Delete Form',
                        subtitle: `Are you sure you want to delete "${form?.title}"? This will also remove all entries. This cannot be undone.`,
                        isDestructive: true,
                        confirmText: 'Delete',
                        onConfirm: () => deleteFormMutation.mutate(),
                    });
                },
            });
        }
        navigation.setOptions({
            headerRight: () => options.length > 0 ? <DropdownMenu options={options} /> : null,
        });
    }, [navigation, form, canEdit, canDelete, deleteFormMutation, lifecycleMutation]);

    const handleSchemaChange = useCallback((newSchema) => {
        setEditScreenData('CreateFormModal', formID, { ...(details || {}), schema: newSchema });
    }, [formID, details]);

    const handleDetailsChange = useCallback((newDetails) => {
        setEditScreenData('CreateFormModal', formID, newDetails);
    }, [formID]);

    // Public forms can never carry directory-dependent fields. Guarded in the
    // settings toggle AND here so a stale working schema can't sneak a profile
    // field into a published public form.
    const publicSchemaGuard = useCallback(() => {
        if (!details?.isPublic) return null;
        if (schemaHasFieldType(details?.schema, 'profile')) {
            return 'Remove profile selector fields before making this form public.';
        }
        // Signatures ARE allowed on public forms — anonymous signers supply
        // their own name/capacity/consent/ink; no directory access needed.
        return null;
    }, [details?.isPublic, details?.schema]);

    const handleSave = useCallback(() => {
        if (!details) return;
        const guardMessage = publicSchemaGuard();
        if (guardMessage) {
            Toast.show({ type: 'error', text2: guardMessage });
            return;
        }
        const { originalValues, ...cleanDetails } = details;
        saveFormMutation.mutate({
            details: { ...cleanDetails, id: formID },
            originalValues,
        });
    }, [details, formID, saveFormMutation, publicSchemaGuard]);

    // True when the edit-screen store has unsaved changes vs. the last saved
    // snapshot (`originalValues`). Used so Publish can save-before-publish only
    // when there's actually something to persist.
    const isDirty = useMemo(() => {
        if (!details || !details.originalValues) return false;
        const rest = { ...details };
        delete rest.originalValues;
        return JSON.stringify(rest) !== JSON.stringify(details.originalValues);
    }, [details]);

    // Block back/gesture navigation while there are unsaved changes.
    // usePreventRemove registers with the native stack so iOS swipe-back and
    // the header back button are intercepted BEFORE the screen dismisses — a
    // plain beforeRemove listener only sees the action after the pop on
    // native-stack. The dirty check still reads the store fresh at event time
    // rather than trusting the `isDirty` condition — e.g. deleteFormMutation
    // clears the store before router.back(), and if the action slips through
    // before the condition re-renders, re-checking lets it proceed silently.
    usePreventRemove(isDirty, ({ data }) => {
        const d = useEditScreenStore.getState().CreateFormModal?.[formID];
        let dirty = false;
        if (d?.originalValues) {
            const rest = { ...d };
            delete rest.originalValues;
            dirty = JSON.stringify(rest) !== JSON.stringify(d.originalValues);
        }
        if (!dirty) {
            navigation.dispatch(data.action);
            return;
        }

        showAlert({
            title: 'Discard unsaved changes?',
            subtitle: "You have changes to this form that haven't been saved.",
            isDestructive: true,
            confirmText: 'Discard',
            cancelText: 'Keep Editing',
            onConfirm: () => navigation.dispatch(data.action),
        });
    });

    // Web: warn on refresh/tab-close while there are unsaved changes.
    useEffect(() => {
        if (!isWeb || typeof window === 'undefined' || !isDirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isWeb, isDirty]);

    // Publish should persist any pending edits first, then flip the status —
    // otherwise the lifecycle mutation overwrites the store with the previously
    // saved snapshot and the user's in-progress changes are lost.
    const handlePublish = useCallback(() => {
        const guardMessage = publicSchemaGuard();
        if (guardMessage) {
            Toast.show({ type: 'error', text2: guardMessage });
            return;
        }
        if (!details || !isDirty) {
            lifecycleMutation.mutate({ action: 'publish' });
            return;
        }
        const { originalValues, ...cleanDetails } = details;
        saveFormMutation.mutate(
            { details: { ...cleanDetails, id: formID }, originalValues },
            { onSuccess: () => lifecycleMutation.mutate({ action: 'publish' }) },
        );
    }, [details, isDirty, formID, saveFormMutation, lifecycleMutation, publicSchemaGuard]);

    // Publish may chain a save before the lifecycle call, so reflect both.
    const publishing = saveFormMutation.isPending || lifecycleMutation.isPending;

    // Create a new path with a FORM_ENTRY trigger, select it on this form, save,
    // then navigate to the path builder so the user can start building immediately.
    const [creatingPath, setCreatingPath] = useState(false);
    const handleCreatePathForForm = useCallback(async () => {
        if (!details || creatingPath) return;
        setCreatingPath(true);
        try {
            // schemaVersion-2 definition (§16.4): trigger pre-bound to THIS
            // form so the entry contract is honest from creation. Created
            // inactive — publish/enroll from the builder.
            const newPath = await createAutomation({
                name: `${details.title || 'Form'} Automation`,
                createdBy: loggedInUser?.id,
                triggerType: 'FORM_ENTRY',
                triggerConfig: { formID },
            });

            // Update the form details with the new path selected
            const updatedDetails = {
                ...details,
                actions: {
                    ...(details.actions || {}),
                    triggerPath: { enabled: true, pathId: newPath.id },
                },
            };
            const { originalValues, ...cleanDetails } = updatedDetails;
            setEditScreenData('CreateFormModal', formID, {
                ...updatedDetails,
                originalValues,
            });

            // Save the form with the new path attached, then navigate once the
            // save lands. The mutation's global onSuccess refreshes originalValues
            // first, so the unsaved-changes guard sees a clean store and the
            // router.push isn't blocked by a discard prompt.
            saveFormMutation.mutate(
                {
                    details: { ...cleanDetails, id: formID },
                    originalValues,
                },
                {
                    onSuccess: () => {
                        Toast.show({ type: 'success', text2: 'Automation created and linked to this form!' });
                        router.push(`/automations/${newPath.id}`);
                    },
                },
            );

            // Refresh the form-trigger paths query so the dropdown populates
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.forFormTrigger() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
        } catch {
            Toast.show({ type: 'error', text2: 'Failed to create automation' });
        } finally {
            setCreatingPath(false);
        }
    }, [details, creatingPath, loggedInUser, formID, saveFormMutation, queryClient, router]);

    // View-only submit handler — calls submit-form edge fn with formId + accessToken
    const handleViewOnlySubmit = useCallback(async (payload, files) => {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token || null;

        const meta = payload?._meta || {};
        const result = await submitForm({
            formId: form.id,
            submissionID: meta.submissionID,
            // Server digest echoed back — a stale revision is a 409
            // `form_changed` when the live schema has signature fields.
            contentRevision: meta.contentRevision ?? form?.contentRevision ?? null,
            payload,
            files,
            accessToken,
            website: meta.honeypot || '',
            form_loaded_at: meta.formLoadedAt || null,
        });

        if (result?.errors) {
            const firstError = Object.values(result.errors)[0];
            Toast.show({ type: 'error', text2: firstError || 'Please fix the highlighted fields.' });
            const err = new Error(firstError || 'Please fix the highlighted fields.');
            // Structured per-field errors map back onto the form fields
            // (keyed by fieldId / repeat instance key) inside FormView.
            err.fieldErrors = result.errors;
            throw err;
        }
        if (result?.error) {
            Toast.show({ type: 'error', text2: result.error });
            throw new Error(result.error);
        }

        queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byFormId(form.id) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.mine() });

        return {
            successMessage: result?.successMessage || form?.successMessage || 'Thanks! Your response has been recorded.',
            payment: result?.payment,
            entryId: result?.entryId,
            redirectUrl: result?.redirectUrl || null,
        };
    }, [form, queryClient]);

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'build':
                return (
                    <View testID="form-build-tab" style={styles.tabScene}>
                        <FormBuildTab
                            formID={formID}
                            schema={details?.schema || form?.schema || createEmptySchema()}
                            onSchemaChange={handleSchemaChange}
                            loggedInUser={loggedInUser}
                            canEdit={canEdit}
                            isPublic={!!(details?.isPublic ?? form?.isPublic)}
                        />
                    </View>
                );
            case 'settings':
                return (
                    <View testID="form-settings-tab" style={styles.tabScene}>
                        <FormSettingsTab
                            details={details || form || {}}
                            onDetailsChange={handleDetailsChange}
                            canEdit={canEdit}
                            onCreatePathForForm={handleCreatePathForForm}
                            creatingPath={creatingPath}
                        />
                    </View>
                );
            case 'share':
                return (
                    <View testID="form-share-tab" style={styles.tabScene}>
                        <FormShareTab
                            form={details || form || {}}
                            onPublish={handlePublish}
                            onClose={() => lifecycleMutation.mutate({ action: 'close' })}
                            onReopen={() => lifecycleMutation.mutate({ action: 'reopen' })}
                            onArchive={() => showAlert({
                                title: 'Archive Form',
                                subtitle: `Archive "${form?.title}"? It will be hidden from members and stop accepting responses. You can unarchive it later — it will return as a draft.`,
                                isDestructive: true,
                                confirmText: 'Archive',
                                onConfirm: () => lifecycleMutation.mutate({ action: 'archive' }),
                            })}
                            onUnarchive={() => lifecycleMutation.mutate({ action: 'unarchive' })}
                            lifecycleSaving={publishing}
                            canEdit={canEdit}
                        />
                    </View>
                );
            case 'entries':
                return (
                    <View testID="form-entries-tab" style={styles.tabScene}>
                        <FormEntriesTab
                            form={details || form || {}}
                            formID={formID}
                            canManageEntries={canManageEntries}
                            router={router}
                        />
                    </View>
                );
            default:
                return null;
        }
    }, [details, form, loggedInUser, canEdit, canManageEntries, handleSchemaChange, handleDetailsChange, handleSave, handlePublish, publishing, saveFormMutation, lifecycleMutation, router, formID, handleCreatePathForForm, creatingPath]);

    const renderTabBar = useCallback((props) => (
        <ReachTabBar {...props} scrollEnabled={false} />
    ), []);

    if (isFetching && !form) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" />
            </View>
        );
    }

    if (!form) {
        return (
            <View style={styles.loadingContainer}>
                <Text style={styles.emptyText}>Form not found</Text>
            </View>
        );
    }

    const status = STATUS_CONFIG[form.status] || STATUS_CONFIG.draft;

    // View-only: user can see/fill but not edit. Show FormView inline without edit UI.
    // Editors can explicitly enter this same experience from the dashboard.
    // In fill mode the outer banner is redundant (FormView renders its own title),
    // and FormView owns scrolling so the whole page scrolls on web + mobile.
    const isFillMode = mode === 'fill';
    if (form.status === 'published' && (isFillMode || !canEdit)) {
        return (
            <View style={styles.container} testID="form-fill-view">
                {!isFillMode && (
                    <View style={styles.headerBar}>
                        <View style={styles.headerLeft}>
                            {isLandscape && (
                                <View style={styles.iconContainer}>
                                    <Unicon name="document-text-outline" size={22} colorVariant="forms" />
                                </View>
                            )}
                            <View style={styles.headerMeta}>
                                <Text style={styles.title}>{form.title}</Text>
                                <View style={styles.statusChipWrap}>
                                    <Chip testID="form-status-chip" colorVariant={status.colorVariant}>{status.label}</Chip>
                                </View>
                            </View>
                        </View>
                    </View>
                )}
                <FormView
                    form={form}
                    isPublic={false}
                    loggedInUser={loggedInUser}
                    // Read-only for members who can see the form but can't submit
                    // (e.g. a "Manage Entries"-only grant on a non-public form).
                    disabled={!!loggedInUser && !canSubmit}
                    onSubmit={handleViewOnlySubmit}
                    // 409 form_changed — refetch so the new contentRevision
                    // reaches FormView; signature/consent answers were already
                    // stripped, ordinary answers preserved.
                    onFormChanged={() => queryClient.invalidateQueries({ queryKey: QueryKeys.forms.byId(formID) })}
                    scrollable
                />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <View style={styles.headerBar}>
                <View style={styles.headerLeft}>
                    {isLandscape && (
                        <View style={styles.iconContainer}>
                            <Unicon name="document-text-outline" size={22} colorVariant="forms" />
                        </View>
                    )}
                    <View style={styles.headerMeta}>
                        <Text style={styles.title}>{form.title}</Text>
                        <View style={styles.statusChipWrap}>
                            <Chip testID="form-status-chip" colorVariant={status.colorVariant}>{status.label}</Chip>
                        </View>
                    </View>
                </View>
                {canEdit && (
                    <View style={styles.headerActions}>
                        {/* Archived forms must be unarchived (→ draft) before they can publish again. */}
                        {(form.status === 'draft' || form.status === 'closed') && (
                            <SolidButton
                                text="Publish"
                                onPress={handlePublish}
                                processing={publishing}
                                type="success"
                                variant="muted"
                                size="sm"
                                iconName="rocket-outline"
                                iconBefore
                                testID="form-publish-header-button"
                            />
                        )}
                        {isDirty && (
                            <SolidButton
                                text="Preview"
                                onPress={() => setPreviewOpen(true)}
                                type="textSecondary"
                                variant="muted"
                                size="sm"
                                iconName="eye-outline"
                                iconBefore
                                testID="form-preview-header-button"
                            />
                        )}
                        <SolidButton
                            text={form.status === 'draft' ? 'Save Draft' : 'Save'}
                            onPress={handleSave}
                            processing={saveFormMutation.isPending}
                            type={form.status === 'draft' ? 'textSecondary' : 'forms'}
                            variant="muted"
                            size="sm"
                            testID="form-save-button"
                        />
                    </View>
                )}
            </View>

            <TabView
                navigationState={{ index: Math.min(tabIndex, tabRoutes.length - 1), routes: tabRoutes }}
                renderScene={renderScene}
                onIndexChange={setTabIndex}
                renderTabBar={renderTabBar}
                swipeEnabled={isNative}
                lazy
            />

            <SheetModal
                isOpen={previewOpen}
                title={`Preview: ${form.title}`}
                cancelText="Close"
                onCancel={() => setPreviewOpen(false)}
                onClose={() => setPreviewOpen(false)}
            >
                <FormView
                    form={details || form}
                    renderMode="preview"
                    isPublic={false}
                    loggedInUser={loggedInUser}
                />
            </SheetModal>
        </View>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1, backgroundColor: 'transparent', paddingTop: 5 },
    loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center' },
    emptyText: {
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: theme.colors.textSecondary,
    },

    headerBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        backgroundColor: theme.colors.surface,
    },
    headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 },
    headerActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    iconContainer: {
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: theme.colors.formsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerMeta: { flex: 1 },
    title: {
        fontSize: 16,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
        marginBottom: 4,
    },
    statusChipWrap: {
        alignSelf: 'flex-start',
    },
    tabScene: { flex: 1 },
}));
